import { z } from 'zod'

import { IEditStoreFormData } from '@/components-latest/ui/AccountPage/EditStoreInfoForm'
import { mapFormToEditStoreRequest } from '@/components-latest/ui/AccountPage/EditStoreInfoForm/helpers'
import ENDPOINTS from '@/config/http/endpoints'
import { IError, parseResponse } from '@/config/http/http.helpers'
import {
  ApiGetPreparationTime,
  DTOBusinessHours
} from '@/ducks/account/account.types'
import { rootApi } from '@/ducks/root.api'

import {
  ApiBusinessDayResponse,
  ApiGetBranchBusinessHoursSchema,
  ApiGetBusinessHoursSchema,
  ApiGetPreparationTimeSchema,
  ApiGetProfileResponseSchema,
  ApiProfileDataSchema
} from '../account.schema'

export const accountApi = rootApi.injectEndpoints({
  endpoints: build => ({
    fetchStoreInfo: build.query<
      z.infer<typeof ApiProfileDataSchema>,
      { storeId: string }
    >({
      providesTags: [{ type: 'Account', id: 'account' }],
      query: ({ storeId }) => ({
        method: 'POST',
        url: ENDPOINTS.GET_STORE_INFO,
        data: { store_id: storeId }
      }),
      transformResponse: (response: z.infer<typeof ApiProfileDataSchema>) => {
        return parseResponse(response, ApiGetProfileResponseSchema).profileData
      }
    }),

    editStoreInfo: build.mutation<
      { message: string; status: string },
      { formData: IEditStoreFormData; store_id: string }
    >({
      invalidatesTags: [{ type: 'Account', id: 'account' }],
      query: ({ formData, store_id }) => ({
        method: 'POST',
        url: ENDPOINTS.EDIT_STORE_INFO,
        data: mapFormToEditStoreRequest(formData, store_id)
      })
    }),

    uploadStoreImage: build.mutation<
      { message: string; status: string; store_img_name?: string },
      { formData: FormData }
    >({
      query: ({ formData }) => ({
        method: 'POST',
        url: ENDPOINTS.UPLOAD_STORE_PICTURE,
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' }
      })
    }),

    fetchPreparationTime: build.query<
      ApiGetPreparationTime,
      { storeId: string }
    >({
      providesTags: [{ type: 'Account', id: 'preparationTime' }],
      query: ({ storeId }) => ({
        method: 'GET',
        url: ENDPOINTS.GET_PREPARATION_TIME,
        params: { store_id: storeId }
      }),
      transformResponse: (response: ApiGetPreparationTime) => {
        return parseResponse(response, ApiGetPreparationTimeSchema)
      }
    }),

    updatePreparationTime: build.mutation<
      { message: string },
      { min: number; max: number; store_id: string }
    >({
      invalidatesTags: [{ type: 'Account', id: 'preparationTime' }],
      query: ({ store_id, max, min }) => ({
        method: 'POST',
        url: ENDPOINTS.UPDATE_PREPARATION_TIME,
        data: { min, max, store_id }
      })
    }),

    fetchBusinessHours: build.query<
      z.infer<typeof ApiGetBusinessHoursSchema>,
      { storeId: string }
    >({
      providesTags: [{ type: 'Account', id: 'HOURS' }],
      query: ({ storeId }) => ({
        method: 'GET',
        url: ENDPOINTS.GET_BUSINESS_HOURS,
        params: { store_id: storeId }
      }),
      transformResponse: (
        response: z.infer<typeof ApiGetBusinessHoursSchema>
      ) => {
        return parseResponse(response, ApiGetBusinessHoursSchema)
      }
    }),

    fetchBranchBusinessHours: build.query<
      z.infer<typeof ApiGetBranchBusinessHoursSchema>[],
      { storeId: string }
    >({
      providesTags: [{ type: 'Account', id: 'HOURS' }],
      query: ({ storeId }) => ({
        method: 'GET',
        url: ENDPOINTS.GET_BRANCH_BUSINESS_HOURS,
        params: { store_id: storeId }
      }),
      transformResponse: (
        response: z.infer<typeof ApiGetBranchBusinessHoursSchema>[]
      ) => {
        return parseResponse(response, z.array(ApiGetBranchBusinessHoursSchema))
      }
    }),

    updateBusinessHours: build.mutation<
      { message: string },
      { storeId: string } & DTOBusinessHours
    >({
      invalidatesTags: [{ type: 'Account', id: 'HOURS' }],
      query: ({ storeId, businessHours }) => ({
        method: 'POST',
        url: ENDPOINTS.POST_BUSINESS_HOURS,
        data: { businessHours, store_id: storeId }
      }),
      transformErrorResponse: (response: IError) => {
        if (response?.cityBusinessHours) {
          const formattedCityBusinessHours = response.cityBusinessHours.map(
            (hour: z.infer<typeof ApiBusinessDayResponse>) => {
              const startTime = new Date(hour.start_time).toLocaleTimeString(
                [],
                {
                  hour: '2-digit',
                  minute: '2-digit'
                }
              )
              const endTime = new Date(hour.end_time).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
              })
              return {
                day_name: hour.day_name,
                start_time: startTime,
                end_time: endTime
              }
            }
          )
          return {
            ...response,
            formattedCityBusinessHours
          }
        }
        return response
      }
    }),

    updateBranchBusinessHours: build.mutation<
      { message: string },
      { storeId: string | undefined } & DTOBusinessHours
    >({
      invalidatesTags: [{ type: 'Account', id: 'HOURS' }],
      query: ({ storeId, businessHours }) => ({
        method: 'POST',
        url: ENDPOINTS.SET_BRANCH_BUSINESS_HOURS,
        data: {
          store_id: storeId,
          businessHours
        }
      }),
      transformErrorResponse: (response: IError) => {
        if (response?.cityBusinessHours) {
          const formattedCityBusinessHours = response.cityBusinessHours.map(
            (hour: z.infer<typeof ApiBusinessDayResponse>) => {
              const startTime = new Date(hour.start_time).toLocaleTimeString(
                [],
                {
                  hour: '2-digit',
                  minute: '2-digit'
                }
              )
              const endTime = new Date(hour.end_time).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
              })
              return {
                day_name: hour.day_name,
                start_time: startTime,
                end_time: endTime
              }
            }
          )
          return {
            ...response,
            formattedCityBusinessHours
          }
        }
        return response
      }
    })
  })
})

const {
  useLazyFetchStoreInfoQuery,
  useEditStoreInfoMutation,
  useUploadStoreImageMutation,
  useLazyFetchPreparationTimeQuery,
  useUpdatePreparationTimeMutation,
  useLazyFetchBusinessHoursQuery,
  useUpdateBusinessHoursMutation,
  useLazyFetchBranchBusinessHoursQuery,
  useUpdateBranchBusinessHoursMutation
} = accountApi

export {
  useEditStoreInfoMutation,
  useLazyFetchBranchBusinessHoursQuery,
  useLazyFetchBusinessHoursQuery,
  useLazyFetchPreparationTimeQuery,
  useLazyFetchStoreInfoQuery,
  useUpdateBranchBusinessHoursMutation,
  useUpdateBusinessHoursMutation,
  useUpdatePreparationTimeMutation,
  useUploadStoreImageMutation
}
